import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Grid, Typography } from '@mui/material';
import { common } from '@mui/material/colors';
import BaseTextMetionsField from 'common/components/base-text-metions';
import FormProvider from 'common/components/form-provider';
import { colors } from 'common/constants';
import { logicDisplayOnTitleSchema } from 'common/schema';
import React, { useEffect } from 'react';
import { FieldError, useForm } from 'react-hook-form';
import { createStyleSheet } from 'utils/helpers';
import { ReactComponent as SaveIcon } from 'assets/images/icons/question-design/save-question.svg';
import CloseIcon from '@mui/icons-material/Close';

interface LogicDisplayOnTitleProps {
    onClose: () => void;
}
const LogicDisplayOnTitle: React.FC<LogicDisplayOnTitleProps> = ({
    onClose,
}) => {
    const defaultTrigger = '$title';
    const randomTrigger = () => {
        return `$${Math.random().toString(36).substring(7)}`;
    };
    const [questionIdForTitle, setQuestionIdForTitle] =
        React.useState<number>();
    const [mentionValue, setMentionValue] = React.useState('');
    const [trigger, setTrigger] = React.useState(defaultTrigger);
    const methods = useForm({
        resolver: yupResolver(logicDisplayOnTitleSchema),
        defaultValues: {
            question: '',
        },
    });

    const { handleSubmit, watch } = methods;

    const watchQuestion = watch('question');

    useEffect(() => {
        // check if mentionValue not in watchQuestion
        if (watchQuestion && watchQuestion.indexOf(mentionValue) === -1) {
            setQuestionIdForTitle(undefined);
            setMentionValue('');
            setTrigger(defaultTrigger);
        }
    }, [watchQuestion, mentionValue]);

    const onSubmit = handleSubmit((data) => {
        console.log(data);
        console.log(questionIdForTitle);
    });

    const customMentionStyle = (error: FieldError | undefined) => {
        return {
            control: {
                fontSize: 16,
                fontWeight: '400',
                minHeight: '56px',
                width: '100%',
                border: error
                    ? `1px solid ${colors.textError}`
                    : '1px solid #ccc',
                borderRadius: '50px',
                outline: 'none',
                backgroundColor: 'white',
                padding: '0 16px',
            },
            input: {
                fontSize: 16,
                fontWeight: '400',
                minHeight: '56px',
                width: '100%',
                border: error
                    ? `1px solid ${colors.textError}`
                    : '1px solid #ccc',
                borderRadius: '50px',
                outline: 'none',
                backgroundColor: 'white',
                padding: '0 16px',
            },
            suggestions: {
                marginLeft: '40px',
                list: {
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    fontSize: 16,
                },
                item: {
                    padding: '8px 16px',
                    borderBottom: '1px solid #ddd',
                    cursor: 'pointer',
                    '&:hover': {
                        backgroundColor: 'black',
                    },
                },
            },
        };
    };

    return (
        <>
            <Typography
                variant="h6"
                sx={{
                    color: common.black,
                    fontWeight: '600',
                    fontSize: '30px',
                }}
            >
                Display Answer on Title
            </Typography>
            <Typography
                variant="body1"
                sx={{
                    color: common.black,
                    fontWeight: '400',
                    fontSize: '14px',
                    marginBottom: '16px',
                }}
            >
                <p>
                    Lấy đáp án đã được lựa chọn ở câu hỏi trước điền vào tiêu đề
                    của câu hỏi này. Chỉ lấy được đáp án từ các câu hỏi sau:
                </p>
                <ol style={{ paddingLeft: '15px' }}>
                    <li>Single choice</li>
                    <li>Text</li>
                    <li>Number</li>
                    <li>Date</li>
                    <li>Slider Scale</li>
                    <li>Score</li>
                    <li>Textbox recommend</li>
                </ol>
            </Typography>
            <FormProvider methods={methods} onSubmit={onSubmit}>
                <Box
                    sx={{
                        backgroundColor: 'var(--color-primary-light-26)',
                        border: `2px dashed ${colors.main}`,
                    }}
                >
                    <Grid container spacing={2} p={3} alignItems={'center'}>
                        <Grid item md={2}>
                            <Box
                                sx={{
                                    fontSize: '24px',
                                    fontWeight: 600,
                                }}
                            >
                                Câu hỏi
                            </Box>
                        </Grid>
                        <Grid item md={10}>
                            <BaseTextMetionsField
                                name="question"
                                trigger={trigger}
                                onAdd={(id, display) => {
                                    setQuestionIdForTitle(Number(id));
                                    if (id) {
                                        setTrigger(randomTrigger());
                                        setMentionValue(`\${${display}}`);
                                    }
                                }}
                                displayTransform={(id, display) =>
                                    `$title"${display}"`
                                }
                                data={[
                                    { id: '1', display: 'HungTT' },
                                    { id: '2', display: 'Thend' },
                                ]}
                                customMentionStyle={customMentionStyle}
                                markup="${__display__}" // eslint-disable-line
                            >
                                <></>
                            </BaseTextMetionsField>
                        </Grid>
                    </Grid>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        mt: 3,
                    }}
                >
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            sx={{
                                ...styles.button,
                                minWidth: '155px',
                                borderWidth: '2px',
                            }}
                            variant="outlined"
                            startIcon={<CloseIcon />}
                            onClick={onClose}
                        >
                            Đóng
                        </Button>
                        <Button
                            sx={{
                                ...styles.button,
                                minWidth: '155px',
                            }}
                            type="submit"
                            variant="contained"
                            startIcon={<SaveIcon />}
                        >
                            Lưu
                        </Button>
                    </Box>
                </Box>
            </FormProvider>
        </>
    );
};

const styles = createStyleSheet({
    button: {
        minWidth: '246px',
        minHeight: '60px',
        borderRadius: '40px',
        textTransform: 'none',
        fontSize: '20px',
        fontWeight: 700,
    },
});

export default LogicDisplayOnTitle;
